import React, { useState } from 'react';
import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import { FolderKanban, Plus, Sparkles, Target } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import { API } from '../governance_v2/constants';
import { PROJECT_STATUS, VERDICT, fmtDate } from './constants';
import ProjectFormDialog from './ProjectFormDialog';
import { ProjectDetail } from './ProjectDetail';
import { MyActivities } from './MyActivities';
import { PortfolioPanel } from './PortfolioPanel';

const ProjectCard = ({ p, onOpen }) => (
  <Card onClick={() => onOpen(p.id)} className="iad-reveal cursor-pointer transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-lg" data-testid={`project-card-${p.id}`}>
    <CardContent className="space-y-3 p-5">
      <div className="flex items-start justify-between gap-3">
        <p className="font-semibold text-slate-900 dark:text-white">{p.title}</p>
        <Badge className={PROJECT_STATUS[p.status]?.cls}>{PROJECT_STATUS[p.status]?.label}</Badge>
      </div>
      <p className="text-xs text-slate-500">{fmtDate(p.start_date)} → {fmtDate(p.end_date)}</p>
      <div className="flex items-center gap-2">
        <div className="h-1.5 flex-1 rounded-full bg-slate-200 dark:bg-slate-800"><div className="h-1.5 rounded-full bg-gradient-to-r from-sky-500 to-emerald-500" style={{ width: `${p.progress}%` }} /></div>
        <span className="text-xs font-semibold" data-testid={`project-progress-${p.id}`}>{p.progress}%</span>
      </div>
      <div className="flex flex-wrap gap-2 text-[11px]">
        <span className="text-slate-500">{p.completed_count}/{p.activity_count} actividades{p.blocked_count ? ` · ${p.blocked_count} bloqueadas` : ''}</span>
        {p.origin?.type === 'proposal' && <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400"><Sparkles className="h-3 w-3" />Propuesta aprobada</span>}
      </div>
      {p.predictions.length > 0 && <div className="flex flex-wrap gap-1">{p.predictions.map((x) => (
        <Badge key={x.id} className={`${VERDICT[x.verdict]?.cls} gap-1`}><Target className="h-3 w-3" />{x.kpi_name}</Badge>))}</div>}
    </CardContent>
  </Card>
);

/** role: 'super_admin' | 'admin' | 'staff' */
export const ProjectsPanel = ({ companyId, role, currentUserId }) => {
  const canManage = role !== 'staff';
  const [openId, setOpenId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [filter, setFilter] = useState('all');
  const [view, setView] = useState('projects');
  const { data, isLoading } = useQuery({
    queryKey: ['projects', companyId],
    queryFn: async () => (await axios.get(`${API}/companies/${companyId}/projects`)).data.items,
    enabled: !!companyId,
  });
  if (openId) return <ProjectDetail companyId={companyId} projectId={openId} canManage={canManage} currentUserId={currentUserId} onBack={() => setOpenId(null)} />;
  const items = (data || []).filter((p) => filter === 'all' || p.status === filter);
  return (
    <div className="space-y-6" data-testid="projects-panel">
      <div className="flex flex-wrap items-center gap-3">
        <FolderKanban className="h-7 w-7 text-sky-500" />
        <div className="mr-auto">
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white">Proyectos y ejecución</h2>
          <p className="text-sm text-slate-500">Cada decisión aprobada se convierte en un proyecto; aquí se mide si la predicción se cumplió.</p>
        </div>
        {canManage && <Button onClick={() => setCreating(true)} data-testid="project-create-btn"><Plus className="mr-2 h-4 w-4" />Nuevo proyecto</Button>}
      </div>
      {!canManage && <MyActivities companyId={companyId} onOpenProject={setOpenId} />}
      {canManage && (
        <div className="inline-flex rounded-lg border border-slate-200 p-1 dark:border-slate-700">
          {[['projects', 'Proyectos'], ['portfolio', 'Cartera (CPM + Forecast)']].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setView(k)} data-testid={`projects-view-${k}`}
              className={`rounded-md px-3 py-1.5 text-sm transition-colors duration-150 ${view === k ? 'bg-sky-500 text-white' : 'text-slate-500 hover:text-slate-900 dark:hover:text-white'}`}>{l}</button>
          ))}
        </div>
      )}
      {view === 'portfolio' ? <PortfolioPanel companyId={companyId} onOpen={setOpenId} /> : <>
      <div className="flex flex-wrap gap-2">
        {[['all', 'Todos'], ...Object.entries(PROJECT_STATUS).map(([k, v]) => [k, v.label])].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setFilter(k)} data-testid={`projects-filter-${k}`}
            className={`rounded-full border px-3 py-1 text-xs transition-colors duration-150 ${filter === k ? 'border-sky-500 bg-sky-500/15 text-sky-700 dark:text-sky-300' : 'border-slate-300 text-slate-500 hover:border-slate-400 dark:border-slate-700'}`}>{l}</button>
        ))}
      </div>
      {isLoading && <p className="text-sm text-slate-400">Cargando proyectos…</p>}
      {!isLoading && !items.length && <p className="py-10 text-center text-sm text-slate-500" data-testid="projects-empty">No hay proyectos{filter !== 'all' ? ' con este estado' : ''}.</p>}
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">{items.map((p) => <ProjectCard key={p.id} p={p} onOpen={setOpenId} />)}</div>
      </>}
      {creating && <ProjectFormDialog companyId={companyId} onClose={() => setCreating(false)} onCreated={setOpenId} />}
    </div>
  );
};
